import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import HiddenTKReport from "./HiddenTKReport";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:  "#A80D2C",
  blue:  "#C4163A",
  teal:  "#7B0F2B",
  gold:  "#D79A1E",
  slate: "#5B677A",
  bg:    "#F5F8FC",
  card:  "#FFFFFF",
  line:  "#DCE5EF",
  ink:   "#1D2733",
};

const M = {
  competing:     "#A80D2C",
  collaborating: "#1A6B3C",
  compromising:  "#7B3F9E",
  avoiding:      "#D79A1E",
  accommodating: "#1B6FA0",
};

// ─── MODE DEFINITIONS ─────────────────────────────────────────────────────────
const MODE_DEFINITIONS = [
  {
    key: "competing", label: "Competing", subtitle: "(Forcing)",
    tagline: '"Assertive & uncooperative — winning-oriented"',
    color: M.competing, max: 12,
    overview: "Is assertive and uncooperative — pursues own concerns at the other person's expense. A power-oriented mode in which one uses rank, argument, or economic leverage to win their position.",
    uses: [
      "When quick, decisive action is vital — e.g. emergencies.",
      "On important issues where unpopular courses of action need implementing — e.g. cost cutting, enforcing rules, discipline.",
      "On issues vital to company welfare when you know you're right.",
      "To protect yourself against people who take advantage of non-competitive behavior.",
    ],
    highWarning: "You may be surrounded by 'yes' people who have learned it's unwise to disagree with you, cutting you off from important information. Subordinates may act more confident than they feel, reducing their ability to learn and share uncertainties.",
    lowWarning:  "You may often feel powerless in situations, struggle to take a firm stand even when you see the need, or hesitate to use legitimate authority — hindering your effectiveness.",
  },
  {
    key: "collaborating", label: "Collaborating", subtitle: "(Problem Solving)",
    tagline: '"Assertive & cooperative — win-win seeker"',
    color: M.collaborating, max: 12,
    overview: "Both assertive and cooperative — works with the other person to find a solution which fully satisfies both parties' concerns. Involves digging into an issue to identify underlying concerns and finding an alternative that meets both sets of needs.",
    uses: [
      "To find an integrative solution when both sets of concerns are too important to be compromised.",
      "When your objective is to learn — e.g. testing your own assumptions, understanding the views of others.",
      "To merge insights from people with different perspectives on a problem.",
      "To gain commitment by incorporating others' concerns into a consensual decision.",
      "To work through hard feelings which have been interfering with an interpersonal relationship.",
    ],
    highWarning: "You may spend time discussing issues in depth that do not deserve it. Collaboration takes time and energy — trivial problems don't require optimal solutions. Overuse sometimes represents a desire to minimize risk by diffusing responsibility or postponing action.",
    lowWarning:  "You may miss collaborative opportunities and deprive yourself of mutual gains. Subordinates may feel uncommitted to your decisions if their concerns aren't incorporated.",
  },
  {
    key: "compromising", label: "Compromising", subtitle: "(Sharing)",
    tagline: '"Middle-ground seeker — split-the-difference"',
    color: M.compromising, max: 12,
    overview: "Intermediate in both assertiveness and cooperativeness. Seeks mutually acceptable solutions that partially satisfy both parties. Falls between competing and accommodating — faster than collaborating but gives up more than competing.",
    uses: [
      "When goals are moderately important, but not worth the effort or disruption of more assertive modes.",
      "When two opponents with equal power are strongly committed to mutually exclusive goals.",
      "To achieve temporary settlements on complex issues.",
      "To arrive at expedient solutions under time pressure.",
      "As a backup when collaboration or competition fails to be successful.",
    ],
    highWarning: "You may concentrate so heavily on the practicalities and tactics of compromise that you lose sight of larger principles, values, or long-term objectives. An emphasis on bargaining can create a cynical climate that undermines interpersonal trust.",
    lowWarning:  "You may find it too difficult or embarrassing to be effective in bargaining situations, or find it hard to make concessions — making it harder to exit mutually destructive deadlocks gracefully.",
  },
  {
    key: "avoiding", label: "Avoiding", subtitle: "(Withdrawal)",
    tagline: '"Unassertive & uncooperative — diplomatic withdrawer"',
    color: M.avoiding, max: 12,
    overview: "Neither assertive nor cooperative — does not immediately pursue own or others' concerns. Diplomatically sidesteps, postpones, or withdraws from threatening situations without addressing the conflict.",
    uses: [
      "When an issue is trivial or of only passing importance, or when other more important issues are pressing.",
      "When you perceive no chance of satisfying your concerns — e.g. low power or very difficult-to-change situations.",
      "When the potential costs of confronting a conflict outweigh the benefits of resolution.",
      "To let people cool down and reduce tensions to a productive level.",
      "When gathering more information outweighs the advantages of an immediate decision.",
      "When others can resolve the conflict more effectively.",
    ],
    highWarning: "People may struggle to get your input on issues, decisions may be made by default, and others may be 'walking on eggshells' around unresolved tensions that need to be faced.",
    lowWarning:  "You may inadvertently hurt others' feelings or stir up hostilities. You may feel harried or overwhelmed because you haven't set priorities around which issues are truly worth raising.",
  },
  {
    key: "accommodating", label: "Accommodating", subtitle: "(Smoothing)",
    tagline: '"Unassertive & cooperative — relationship preserver"',
    color: M.accommodating, max: 12,
    overview: "Unassertive and cooperative — the opposite of competing. Neglects own concerns to satisfy the concerns of the other person. There is an element of self-sacrifice in this mode: selfless generosity, yielding to another's view, or obeying when one would prefer not to.",
    uses: [
      "When you realize that you are wrong — to allow a better position to be heard and to show that you are reasonable.",
      "When the issue is much more important to the other person than to yourself.",
      "To build up social credits for later issues which are important to you.",
      "When continued competition would only damage your cause — when you are outmatched.",
      "When preserving harmony and avoiding disruption are especially important.",
      "To aid in the development of subordinates by allowing them to experiment and learn from mistakes.",
    ],
    highWarning: "Your own ideas and concerns may not get the attention they deserve, reducing your influence, respect, and recognition. Discipline may also become lax.",
    lowWarning:  "You may have trouble building goodwill with others, be seen as unreasonable, struggle to admit when you are wrong, or find it hard to know when to give up.",
  },
];

// ─── PERCENTILE BANDS ─────────────────────────────────────────────────────────
const PERCENTILE_BANDS = {
  competing:     { p25: 4, p75: 8 },
  collaborating: { p25: 5, p75: 9 },
  compromising:  { p25: 4, p75: 8 },
  avoiding:      { p25: 3, p75: 7 },
  accommodating: { p25: 4, p75: 8 },
};

function scoreBandLabel(key, score) {
  const band = PERCENTILE_BANDS[key];
  if (!band) return "—";
  if (score >= band.p75) return "High Use";
  if (score >= band.p25) return "Moderate Use";
  return "Low Use";
}

// ─── DATE FORMATTER ───────────────────────────────────────────────────────────
function formatDate(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ─── DOMINANT MODE DERIVATION ─────────────────────────────────────────────────
function getDominantModes(scores) {
  if (!scores) return [];
  const sorted = Object.entries(scores)
    .filter(([k]) => MODE_DEFINITIONS.some(m => m.key === k))
    .sort(([, a], [, b]) => b - a);
  const top = sorted[0]?.[1] ?? 0;
  return sorted.filter(([, v]) => v >= top - 1).map(([k]) => k);
}

// ─── HELPER COMPONENTS ────────────────────────────────────────────────────────
function Skeleton({ h = 24, w = "100%", r = 8 }) {
  return (
    <div style={{
      height: h, width: w, borderRadius: r,
      background: "linear-gradient(90deg,#EAF0F6 25%,#f5f8fc 50%,#EAF0F6 75%)",
      backgroundSize: "200% 100%",
      animation: "tk-shimmer 1.4s infinite",
    }} />
  );
}

function Callout({ children }) {
  return (
    <div style={{
      background: "linear-gradient(180deg,#fff9e8,#fffdf2)",
      border: "1px solid #F2D58A",
      borderRadius: 16,
      padding: "14px 16px",
      fontSize: "var(--tk-text-sm)",
      lineHeight: 1.6,
      color: T.ink,
      textAlign: "justify",
    }}>
      {children}
    </div>
  );
}

function ModeCard({ def, score, band, isDominant }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      borderTop: isDominant ? `1.5px solid ${def.color}` : `1px solid ${open ? def.color + "55" : T.line}`,
      borderRight: isDominant ? `1.5px solid ${def.color}` : `1px solid ${open ? def.color + "55" : T.line}`,
      borderBottom: isDominant ? `1.5px solid ${def.color}` : `1px solid ${open ? def.color + "55" : T.line}`,
      borderLeft: `5px solid ${def.color}`,
      borderRadius: 16,
      overflow: "hidden",
      transition: "border-color .2s",
      boxShadow: isDominant ? `0 0 0 3px ${def.color}18` : "none",
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "clamp(10px,2vw,14px) clamp(12px,2.5vw,18px)",
          background: open ? `${def.color}0A` : isDominant ? `${def.color}07` : "#fbfdff",
          border: "none",
          cursor: "pointer",
          gap: 12,
          textAlign: "left",
          transition: "background .2s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: def.color, flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: def.color, fontSize: "var(--tk-text-sm)" }}>
              {def.label} <span style={{ fontWeight: 400, color: T.slate, fontSize: "var(--tk-text-xs)" }}>{def.subtitle}</span>
              {isDominant && (
                <span style={{
                  marginLeft: 8, fontSize: "var(--tk-text-xs)", fontWeight: 700,
                  color: def.color, background: `${def.color}18`,
                  padding: "2px 8px", borderRadius: 999, whiteSpace: "nowrap",
                }}>★ DOMINANT MODE</span>
              )}
            </div>
            <div style={{ fontSize: "var(--tk-text-xs)", color: T.slate, fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{def.tagline}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {score !== null && (
            <span style={{ fontWeight: 700, fontSize: "clamp(0.9rem,2vw,1.1rem)", color: def.color }}>{score}/12</span>
          )}
          {band && (
            <span style={{
              fontSize: "var(--tk-text-xs)", fontWeight: 700, color: def.color,
              textTransform: "uppercase", letterSpacing: ".05em",
              background: `${def.color}14`, padding: "3px 8px", borderRadius: 999,
              display: "none",
            }} className="tk-band-pill">{band}</span>
          )}
          <span style={{ color: def.color, fontSize: "1.2rem", transform: open ? "rotate(180deg)" : "none", transition: "transform .25s" }}>▾</span>
        </div>
      </button>

      {open && (
        <div style={{ padding: "clamp(10px,2vw,14px) clamp(12px,2.5vw,18px) clamp(14px,2.5vw,18px)", background: "#fff", borderTop: `1px solid ${T.line}` }}>
          <p style={{ margin: "0 0 10px", fontSize: "var(--tk-text-sm)", lineHeight: 1.65, color: T.ink, textAlign: "justify" }}>{def.overview}</p>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: "var(--tk-text-xs)", color: def.color, marginBottom: 6 }}>When to use this mode:</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {def.uses.map((u, i) => <li key={i} style={{ fontSize: "var(--tk-text-xs)", color: T.ink, marginBottom: 3, lineHeight: 1.5 }}>{u}</li>)}
            </ul>
          </div>
          <div className="tk-warn-grid">
            <div style={{ background: "#fff9e8", border: "1px solid #F2D58A", borderRadius: 10, padding: "clamp(8px,1.5vw,12px)" }}>
              <div style={{ fontWeight: 700, color: T.gold, fontSize: "var(--tk-text-xs)", marginBottom: 4 }}>⚠ If you scored High</div>
              <p style={{ margin: 0, fontSize: "var(--tk-text-xs)", color: T.ink, lineHeight: 1.5 }}>{def.highWarning}</p>
            </div>
            <div style={{ background: "#f0f7ff", border: "1px solid #C0D8EF", borderRadius: 10, padding: "clamp(8px,1.5vw,12px)" }}>
              <div style={{ fontWeight: 700, color: "#1B6FA0", fontSize: "var(--tk-text-xs)", marginBottom: 4 }}>⚠ If you scored Low</div>
              <p style={{ margin: 0, fontSize: "var(--tk-text-xs)", color: T.ink, lineHeight: 1.5 }}>{def.lowWarning}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PENTAGON RADAR CHART ─────────────────────────────────────────────────────
function TKRadarChart({ scores, loading }) {
  const size = 300;
  const cx = size / 2, cy = size / 2, r = 90, maxScore = 12;
  const modes = ["competing", "collaborating", "compromising", "avoiding", "accommodating"];
  const modeLabels = ["Competing", "Collaborating", "Compromising", "Avoiding", "Accommodating"];
  const n = modes.length;
  const angleStep = (2 * Math.PI) / n;
  const startAngle = -Math.PI / 2;

  function vertex(i, radius) {
    const angle = startAngle + i * angleStep;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  }

  const rings = [3, 6, 9, 12];
  const scorePoints = modes.map((m, i) => {
    const val = loading ? 0 : (scores?.[m] ?? 0);
    const v = vertex(i, (val / maxScore) * r);
    return `${v.x},${v.y}`;
  }).join(" ");
  const axes = modes.map((_, i) => vertex(i, r));

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: size, display: "block", margin: "0 auto" }}>
      {rings.map(val => {
        const pts = modes.map((_, i) => { const v = vertex(i, (val/maxScore)*r); return `${v.x},${v.y}`; }).join(" ");
        return <polygon key={val} points={pts} fill="none" stroke={T.line} strokeWidth={1} />;
      })}
      {rings.map(val => {
        const v = vertex(0, (val/maxScore)*r);
        return <text key={val} x={v.x + 4} y={v.y - 3} fontSize="7.5" fill={T.slate}>{val}</text>;
      })}
      {axes.map((v, i) => <line key={i} x1={cx} y1={cy} x2={v.x} y2={v.y} stroke={T.line} strokeWidth={1.2} />)}
      <polygon points={scorePoints} fill={T.navy} fillOpacity={loading ? 0 : 0.18} stroke={T.navy} strokeWidth={2} strokeLinejoin="round" style={{ transition: "all .8s ease" }} />
      {!loading && modes.map((m, i) => {
        const val = scores?.[m] ?? 0;
        const v = vertex(i, (val/maxScore)*r);
        return <circle key={m} cx={v.x} cy={v.y} r={4} fill={M[m]} stroke="#fff" strokeWidth={1.5} />;
      })}
      {axes.map((v, i) => {
        const lv = vertex(i, r + 22);
        return (
          <text key={i} x={lv.x} y={lv.y + 4} textAnchor="middle" fontSize="9" fontWeight="700" fill={M[modes[i]]} letterSpacing=".04em">
            {modeLabels[i]}
          </text>
        );
      })}
      <circle cx={cx} cy={cy} r={3} fill={T.slate} />
    </svg>
  );
}

// ─── ASSERTIVENESS / COOPERATIVENESS POSITION GRID ───────────────────────────
function TKPositionGrid({ scores }) {
  const modes = [
    { key: "competing",     label: "COMPETING",     x: 80,  y: 50,  color: M.competing     },
    { key: "collaborating", label: "COLLABORATING", x: 490, y: 50,  color: M.collaborating },
    { key: "compromising",  label: "COMPROMISING",  x: 285, y: 215, color: M.compromising  },
    { key: "avoiding",      label: "AVOIDING",      x: 80,  y: 378, color: M.avoiding      },
    { key: "accommodating", label: "ACCOMMODATING", x: 490, y: 378, color: M.accommodating },
  ];
  const bw = 190, bh = 148, svgW = 760, svgH = 610;
  const maxScore = scores ? Math.max(...Object.values(scores)) : 0;
  const axisX = 42, axisTop = 30, axisBot = 555, axisH = axisBot - axisTop;
  const axisY = 568, axisLeft = 58, axisRight = 728, axisW = axisRight - axisLeft;
  const maxAxisScore = 24;
  const assertScore = scores ? (scores.competing ?? 0) + (scores.collaborating ?? 0) : null;
  const coopScore   = scores ? (scores.collaborating ?? 0) + (scores.accommodating ?? 0) : null;
  const assertDotY  = assertScore !== null ? axisBot - (assertScore / maxAxisScore) * axisH : null;
  const coopDotX    = coopScore   !== null ? axisLeft + (coopScore / maxAxisScore) * axisW  : null;
  const pointX = coopDotX, pointY = assertDotY;
  let activeMode = null;
  if (pointX !== null && pointY !== null) {
    const midX = (axisLeft + axisRight) / 2, midY = (axisTop + axisBot) / 2;
    const dx = Math.abs(pointX - midX), dy = Math.abs(pointY - midY);
    if (dx < 80 && dy < 80)             activeMode = "compromising";
    else if (pointY < midY && pointX < midX) activeMode = "competing";
    else if (pointY < midY && pointX > midX) activeMode = "collaborating";
    else if (pointY > midY && pointX < midX) activeMode = "avoiding";
    else                                     activeMode = "accommodating";
  }

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ maxWidth: 680, margin: "0 auto", display: "block", overflow: "visible" }}>
      {[0,6,12,18,24].map(val => <line key={`h-${val}`} x1={axisLeft} y1={axisBot-(val/maxAxisScore)*axisH} x2={axisRight} y2={axisBot-(val/maxAxisScore)*axisH} stroke="#eee" />)}
      {[0,6,12,18,24].map(val => <line key={`v-${val}`} x1={axisLeft+(val/maxAxisScore)*axisW} y1={axisTop} x2={axisLeft+(val/maxAxisScore)*axisW} y2={axisBot} stroke="#eee" />)}
      <line x1={axisX} y1={axisTop} x2={axisX} y2={axisBot} stroke="#555" strokeWidth={1.5} />
      <line x1={axisLeft} y1={axisY} x2={axisRight} y2={axisY} stroke="#555" strokeWidth={1.5} />
      <text x={10} y={(axisTop+axisBot)/2} textAnchor="middle" fontSize="11" fontWeight="700" fill={M.competing} transform={`rotate(-90,10,${(axisTop+axisBot)/2})`} style={{ fontFamily:"Arial,sans-serif", letterSpacing:"2px" }}>ASSERTIVENESS</text>
      <text x={axisX-5} y={axisTop+8} fontSize="8" textAnchor="end" fill="#555">Assertive</text>
      <text x={axisX-5} y={axisBot-4} fontSize="8" textAnchor="end" fill="#555">Unassertive</text>
      <text x={(axisLeft+axisRight)/2} y={svgH-8} textAnchor="middle" fontSize="11" fontWeight="700" fill={M.collaborating} style={{ fontFamily:"Arial,sans-serif", letterSpacing:"2px" }}>COOPERATIVENESS</text>
      <text x={axisLeft+30} y={axisY+16} fontSize="8" textAnchor="middle" fill="#555">Uncooperative</text>
      <text x={axisRight-30} y={axisY+16} fontSize="8" textAnchor="middle" fill="#555">Cooperative</text>
      {[0,6,12,18,24].map(val => {
        const y = axisBot-(val/maxAxisScore)*axisH;
        return <g key={`yl-${val}`}><line x1={axisX-4} y1={y} x2={axisX+4} y2={y} stroke="#666" /><text x={axisX-8} y={y+3} fontSize="8" textAnchor="end" fill="#555">{val}</text></g>;
      })}
      {[0,6,12,18,24].map(val => {
        const x = axisLeft+(val/maxAxisScore)*axisW;
        return <g key={`xl-${val}`}><line x1={x} y1={axisY-4} x2={x} y2={axisY+4} stroke="#666" /><text x={x} y={axisY+14} fontSize="8" textAnchor="middle" fill="#555">{val}</text></g>;
      })}
      {assertDotY !== null && <circle cx={axisX} cy={assertDotY} r={7} fill={T.navy} />}
      {coopDotX   !== null && <circle cx={coopDotX} cy={axisY}   r={7} fill={M.collaborating} />}
      {pointX !== null && pointY !== null && (
        <g>
          <line x1={pointX} y1={axisTop} x2={pointX} y2={axisBot} stroke="#bbb" strokeDasharray="4 4" />
          <line x1={axisLeft} y1={pointY} x2={axisRight} y2={pointY} stroke="#bbb" strokeDasharray="4 4" />
          <circle cx={pointX} cy={pointY} r={10} fill={T.ink} />
        </g>
      )}
      {modes.map(({ key, label, x, y, color }) => {
        const score = scores?.[key] ?? null;
        const isDominant  = scores && score === maxScore && maxScore > 0;
        const isActive = key === activeMode;
        return (
          <g key={key}>
            <rect x={x} y={y} width={bw} height={bh} rx={8}
              fill={isActive ? `${color}22` : "#fff"}
              stroke={isActive ? color : (isDominant ? color : "#8B9B7A")}
              strokeWidth={isActive ? 3.5 : (isDominant ? 2.5 : 1.5)}
            />
            <text x={x+bw/2} y={y+30} textAnchor="middle" fontSize="10" fontWeight="700" fill={color} style={{ fontFamily:"Arial,sans-serif", letterSpacing:"1px" }}>{label}</text>
            {score !== null && (
              <>
                <text x={x+bw/2} y={y+78} textAnchor="middle" fontSize="30" fontWeight="800" fill={color} style={{ fontFamily:"Arial,sans-serif" }}>{score}</text>
                <text x={x+bw/2} y={y+98} textAnchor="middle" fontSize="9" fill="#999">out of 12</text>
              </>
            )}
            {isDominant && <text x={x+bw/2} y={y+126} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={color} style={{ fontFamily:"Arial,sans-serif", letterSpacing:".5px" }}>★ DOMINANT</text>}
          </g>
        );
      })}
    </svg>
  );
}

// function formatDate(date) {
//   return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
// }

// ─── GRAPHING TKI SCORES (Percentile Chart) ───────────────────────────────────
function TKPercentileChart({ scores }) {
  const modeKeys   = ["competing","collaborating","compromising","avoiding","accommodating"];
  const modeLabels = ["Competing","Collaborating","Compromising","Avoiding","Accommodating"];
  const svgW = 700, svgH = 490;
  const colW = 96, colGap = 14;
  const chartLeft = 76, chartTop = 52, chartBottom = 410;
  const chartH = chartBottom - chartTop;
  const CUTS = {
    competing:     { lowMax: 3, midMax: 7 },
    collaborating: { lowMax: 5, midMax: 8 },
    compromising:  { lowMax: 4, midMax: 8 },
    avoiding:      { lowMax: 3, midMax: 6 },
    accommodating: { lowMax: 4, midMax: 7 },
  };

  function scoreToY(score, cut) {
    const { lowMax, midMax } = cut;
    const low = chartH * 0.25, mid = chartH * 0.5, high = chartH * 0.25;
    if (score <= lowMax) return chartBottom - (score / lowMax) * low;
    if (score <= midMax) return chartBottom - low - ((score - lowMax) / (midMax - lowMax)) * mid;
    return chartTop + (1 - (score - midMax) / (12 - midMax)) * high;
  }

  const cols = modeKeys.map((key, i) => ({
    key, label: modeLabels[i], color: M[key],
    x: chartLeft + i * (colW + colGap),
    score: scores?.[key] ?? null,
    cut: CUTS[key],
  }));

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" style={{ maxWidth: 680, margin: "0 auto", display: "block" }}>
      <defs>
        {cols.map(({ key, color }) => (
          <linearGradient key={key} id={`tkr-grad-${key}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.65" />
            <stop offset="100%" stopColor={color} stopOpacity="0.15" />
          </linearGradient>
        ))}
      </defs>
      <text x={svgW/2} y={22} textAnchor="middle" fontSize="13" fontWeight="700" fill={T.ink}>GRAPHING YOUR TKI SCORES</text>
      {[
        { label: "HIGH 25%",   y: chartTop  + chartH * 0.125 },
        { label: "MIDDLE 50%", y: chartTop  + chartH * 0.5   },
        { label: "LOW 25%",    y: chartBottom - chartH * 0.125 },
      ].map(({ label, y }) => (
        <text key={label} x={18} y={y} fontSize="8" fontWeight="700" fill={T.slate} textAnchor="middle" transform={`rotate(-90,18,${y})`}>{label}</text>
      ))}
      {cols.map(({ key, label, color, x, score, cut }) => {
        const dotY  = score !== null ? scoreToY(score, cut) : null;
        const lowY  = scoreToY(cut.lowMax, cut);
        const midY  = scoreToY(cut.midMax, cut);
        return (
          <g key={key}>
            {score !== null && <rect x={x} y={dotY} width={colW} height={chartBottom - dotY} fill={`url(#tkr-grad-${key})`} rx="6" />}
            <rect x={x} y={chartTop} width={colW} height={chartH} fill="none" stroke={T.line} />
            <line x1={x} y1={lowY} x2={x+colW} y2={lowY} stroke={color} strokeDasharray="4,3" opacity={0.7} />
            <line x1={x} y1={midY} x2={x+colW} y2={midY} stroke={color} strokeDasharray="4,3" opacity={0.7} />
            {Array.from({ length: 13 }, (_, s) => (
              <text key={s} x={x+colW/2} y={scoreToY(s,cut)+4} fontSize="9" fill="#374151" textAnchor="middle">{s}</text>
            ))}
            {dotY !== null && (
              <g>
                <circle cx={x+colW/2} cy={dotY} r={14} fill={color} />
                <text x={x+colW/2} y={dotY+4} fontSize="12" fontWeight="700" fill="#fff" textAnchor="middle">{score}</text>
              </g>
            )}
            <text x={x+colW/2} y={chartTop-10} fontSize="9" fontWeight="700" fill={color} textAnchor="middle">{label}</text>
          </g>
        );
      })}
      <text x={svgW/2} y={svgH-6} fontSize="7.5" fill={T.slate} textAnchor="middle">Scores visualised with percentile zones and dynamic performance fill.</text>
    </svg>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function TKResults() {
  const location = useLocation();
  const navState = location.state || {};

  const [reportMeta,  setReportMeta]  = useState(null);
  const [scores,      setScores]      = useState(null);
  const [insightText, setInsightText] = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [pdfData,     setPdfData]     = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [submissionTime, setSubmissionTime] = useState(null);
  const generatingRef = useRef(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        if (navState?.scores) {
          const tkData = navState.scores;
          const m = tkData.modes ?? tkData;
          const s = {
            competing:     Number(m.competing?.score     ?? m.competing     ?? 0),
            collaborating: Number(m.collaborative?.score ?? m.collaborating ?? m.collaborating?.score ?? 0),
            compromising:  Number(m.compromising?.score  ?? m.compromising  ?? 0),
            avoiding:      Number(m.avoiding?.score      ?? m.avoiding      ?? 0),
            accommodating: Number(m.accommodating?.score ?? m.accommodating ?? 0),
          };
          const dominant = getDominantModes(s);
          const dominantLabels = dominant.map(k => MODE_DEFINITIONS.find(d => d.key === k)?.label ?? k).join(" & ");
          const dateStr = tkData.submission_time
            ? formatDate(new Date(tkData.submission_time))
            : formatDate(new Date());

          setReportMeta({ orgName: navState.name || tkData.userInfo?.name || tkData.name || "Participant", period: dateStr, reportTitle: "Thomas-Kilmann Conflict Mode Assessment" });
          setScores(s);
          setInsightText(`Your conflict mode profile indicates a primary tendency towards ${dominantLabels}. Each of the five modes has its appropriate context — your repertoire reflects the mix of situations you encounter.`);
          setSubmissionTime(tkData.submission_time ?? new Date().toISOString());
          return;
        }
        const email = navState?.email ?? null;
        const phone = navState?.phone ?? null;
        
        if (!email && !phone) { setError("No result data found. Please complete the survey first."); return; }
        const API_BASE = process.env.REACT_APP_API_BASE_URL ?? "";
        const res = await fetch(`${API_BASE}/api/calculate-score/tk`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, phone }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error ?? `Server error ${res.status}`); }
        const json = await res.json();
        if (!json.success) throw new Error(json.error ?? "Unexpected response");
        const tkData = json.data;
        const s = {
          competing:     Number(tkData.competing     ?? 0),
          collaborating: Number(tkData.collaborating ?? 0),
          compromising:  Number(tkData.compromising  ?? 0),
          avoiding:      Number(tkData.avoiding      ?? 0),
          accommodating: Number(tkData.accommodating ?? 0),
        };
        const dominant = getDominantModes(s);
        const dominantLabels = dominant.map(k => MODE_DEFINITIONS.find(d => d.key === k)?.label ?? k).join(" & ");
        const dateStr = tkData.submission_time
          ? formatDate(new Date(tkData.submission_time))
          : formatDate(new Date());
        setReportMeta({ orgName: tkData.name || navState.name || "Participant", period: dateStr, reportTitle: "Thomas-Kilmann Conflict Mode Assessment" });
        setScores(s);
        setInsightText(`Your conflict mode profile indicates a primary tendency towards ${dominantLabels}. Each of the five modes has its appropriate context — your repertoire reflects the mix of situations you encounter.`);
        setSubmissionTime(tkData.submission_time ?? new Date().toISOString());
      } catch (e) {
        setError(e.message || "Failed to load report data.");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const dominantModes  = getDominantModes(scores);
  const dominantLabels = dominantModes.map(k => MODE_DEFINITIONS.find(d => d.key === k)?.label ?? k).join(" & ");
  // const dateStr = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const handleDownloadPDF = useCallback(() => {
    if (loading || !scores || generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    setPdfData({
      userInfo:        { name: reportMeta?.orgName || "Participant" },
      scores,
      insightText,
      dominantModes,
      submission_time: submissionTime,
    });
  }, [loading, scores, insightText, dominantModes, reportMeta, submissionTime]);

  const handlePdfComplete = useCallback(() => {
    setPdfData(null);
    setIsGenerating(false);
    generatingRef.current = false;
  }, []);

  if (loading && !scores) return (
    <div style={{ padding: "2rem", textAlign: "center", fontFamily: "'Satoshi', sans-serif" }}>Loading Report…</div>
  );
  if (error) return (
    <div style={{ padding: "2rem", color: "red", fontFamily: "'Satoshi', sans-serif" }}>Error: {error}</div>
  );

  return (
    <>
      <div className="tk-report" id="tk-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
          :root {
            --tk-text-xs:   clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem);
            --tk-text-sm:   clamp(0.875rem, 0.8rem + 0.35vw, 1rem);
            --tk-text-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
            --tk-text-lg:   clamp(1.125rem, 1rem + 0.75vw, 1.4rem);
            --tk-text-xl:   clamp(1.45rem, 1.2rem + 1.1vw, 2rem);
            --tk-space-4: 1rem; --tk-space-6: 1.5rem; --tk-space-8: 2rem;
            --tk-space-10: 2.5rem; --tk-space-12: 3rem; --tk-space-16: 4rem;
            --tk-navy: #A80D2C; --tk-blue: #C4163A; --tk-gold: #D79A1E;
            --tk-ink: #1D2733; --tk-muted: #5B677A;
            --tk-bg: #f4f6f8; --tk-card: #ffffff; --tk-line: #DCE5EF;
            --tk-radius: 20px; --tk-radius-sm: 12px;
            --tk-shadow: 0 18px 40px rgba(14,39,68,.10);
            --tk-font: 'Satoshi', sans-serif;

            /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
          }

          /* ── Root ── */
          .tk-report {
            font-family: var(--tk-font);
            background: linear-gradient(180deg, #eef4f9 0%, #f7fafc 100%);
            color: var(--tk-ink);
            line-height: 1.55;
          }
          .tk-report .report {
            max-width: 1100px;
            margin: 0 auto;
            padding: var(--tk-space-8) var(--tk-space-4) var(--tk-space-16);
          }

          /* ── Page shells ── */
          .tk-report .page {
            background: var(--tk-card);
            border: 1px solid var(--tk-line);
            border-radius: 28px;
            box-shadow: var(--tk-shadow);
            overflow: hidden;
            margin-bottom: var(--tk-space-8);
          }

          /* ── Cover ── */
          .tk-report .cover {
            min-height: 92vh;
            position: relative;
            background: radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%),
                        linear-gradient(135deg, #A81026, #C4163A 55%, #7B0F2B);
            color: white;
          }
          .tk-report .cover-inner {
            padding: var(--tk-space-16);
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            min-height: 92vh;
          }
          .tk-report .brand {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: var(--tk-space-4);
            position: relative;
            z-index: 2;
            padding-right: clamp(0px, 38vw, 200px);
          }
          .tk-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
         .tk-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .tk-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
            .tk-report .badge {
            border: 1px solid rgba(255,255,255,.18);
            background: rgba(255,255,255,.08);
            padding: clamp(.3rem,.8vw,.6rem) clamp(.5rem,1.2vw,1rem);
            border-radius: 999px;
            font-size: clamp(0.58rem,1.5vw,0.85rem);
            white-space: nowrap; flex-shrink: 0;
          }
          .tk-report .cover-title { margin-top: var(--tk-space-16); }
          .tk-report .cover-title h2 {
            margin: 0 0 var(--tk-space-4);
            font-size: clamp(2.2rem, 5vw, 4.4rem);
            line-height: .96; color: #fff;
          }
          .tk-report .cover-title p {
            font-size: var(--tk-text-lg);
            max-width: 100%;
            color: rgba(255,255,255,.84);
            text-align: justify;
          }
          .tk-report .cover-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: var(--tk-space-4);
            margin-top: var(--tk-space-12);
          }
          .tk-report .meta {
            background: rgba(255,255,255,.08);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: 18px;
            padding: 1rem 1.1rem;
          }
          .tk-report .meta span   { display: block; font-size: var(--tk-text-xs); color: rgba(255,255,255,.66); margin-bottom: .25rem; }
          .tk-report .meta strong { font-size: var(--tk-text-base); color: white; }
          .tk-report .cover-badges { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: var(--tk-space-8); }
          .tk-report .cover-badge {
            background: rgba(255,255,255,.12);
            border: 1px solid rgba(255,255,255,.18);
            padding: .5rem .9rem; border-radius: 999px; font-size: 0.95rem;
          }
          .tk-report .footer-note {
            display: flex; justify-content: space-between; gap: var(--tk-space-4);
            color: rgba(255,255,255,.72); font-size: var(--tk-text-xs);
            margin-top: var(--tk-space-10);
          }

          /* ── Inner pages ── */
          .tk-report .page-inner { padding: var(--tk-space-10); }
          .tk-report .topbar {
            display: flex; justify-content: space-between; align-items: flex-start;
            gap: var(--tk-space-4); margin-bottom: var(--tk-space-8);
          }
          .tk-report .section-kicker {
            color: #c62828; font-size: var(--tk-text-xs);
            text-transform: uppercase; letter-spacing: .12em; font-weight: 700;
          }
          .tk-report .section-title { margin: .35rem 0 0; font-size: var(--tk-text-xl); color: #0f2744; }
          .tk-report .pill {
            display: inline-flex; align-items: center; gap: .45rem;
            padding: .5rem .85rem; border-radius: 999px;
            font-size: var(--tk-text-xs); font-weight: 700;
            text-transform: uppercase; letter-spacing: .06em; white-space: nowrap;
          }
          .tk-report .pill-red   { background: #fef2f2; color: #c62828; }
          .tk-report .pill-slate { background: #f1f5f9; color: #5B677A; }

          /* ── Cards ── */
          .tk-report .card {
            background: linear-gradient(180deg, #fff 0%, #fbfcfd 100%);
            border: 1px solid var(--tk-line); border-radius: var(--tk-radius);
            padding: var(--tk-space-6);
          }
          .tk-report .card h3 { margin: 0 0 .75rem; font-size: var(--tk-text-lg); color: #0f2744; }
          .tk-report .lead { color: #334155; font-size: var(--tk-text-base); margin: 0; text-align: justify; }
          .tk-report .summary-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }

          /* ── Sub-panel (two-col inner) ── */
          .tk-report .sub-panel {
            background: #fbfdff; border: 1px solid var(--tk-line);
            border-radius: 16px; padding: 1.25rem;
          }
          .tk-report .sub-panel h4 { margin: 0 0 .65rem; font-size: var(--tk-text-base); color: #0f2744; }
          .tk-report .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }

          /* ── Score bars (VAK-style: taller tracks, larger gap) ── */
          .tk-report .bars { display: grid; gap: 1.5rem; margin-top: 1rem; }
          .tk-report .bar-row { display: grid; grid-template-columns: 175px 1fr 60px; gap: 1rem; align-items: center; margin-bottom: .8rem; }
          .tk-report .bar-label { font-size: var(--tk-text-base); font-weight: 700; color: #0f2744; }
          .tk-report .bar-track { height: 24px; background: #e9eef3; border-radius: 999px; overflow: hidden; }
          .tk-report .bar-fill  { height: 100%; border-radius: 999px; transition: width .8s cubic-bezier(.4,0,.2,1); }
          .tk-report .bar-val   { text-align: right; font-size: var(--tk-text-base); font-weight: 700; font-variant-numeric: tabular-nums; }

          /* ── Score tiles ── */
          .tk-report .score-tile-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 8px; }
          .tk-report .score-tile {
            border-radius: 14px; border: 1px solid var(--tk-line);
            padding: .7rem .85rem;
            background: linear-gradient(180deg,#fff,#f8fbff); min-width: 0;
          }
          .tk-report .score-tile-label { font-size: .72rem; letter-spacing: .08em; text-transform: uppercase; color: var(--tk-muted); font-weight: 700; margin-bottom: 2px; }
          .tk-report .score-tile-value { font-size: 1.7rem; font-weight: 800; line-height: 1.1; }
          .tk-report .score-tile-band  { font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-top: 2px; }

          /* ── Growth cards ── */
          .tk-report .growth-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px,1fr)); gap: .9rem; margin-bottom: 1.1rem; }
          .tk-report .growth-card { border-radius: 12px; padding: .9rem 1rem; border: 1px solid var(--tk-line); background: #fbfdff; }
          .tk-report .growth-card-title { font-size: var(--tk-text-sm); font-weight: 700; margin-bottom: .4rem; }
          .tk-report .growth-card p { font-size: var(--tk-text-xs); color: #334155; margin: 0; line-height: 1.55; }

          /* ── Mode warn grid ── */
          .tk-warn-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; margin-top: .6rem; }

          /* ── Back cover ── */
          .tk-report .back-cover { color: white; }
          .tk-report .back-cover .page-inner { display: flex; flex-direction: column; justify-content: space-between; }
          .tk-report .back-cover-header {
            display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center;
            gap: .5rem; padding: clamp(.9rem,2.5vw,1.5rem) clamp(.9rem,3vw,2.5rem);
            border-bottom: 1px solid rgba(255,255,255,0.15);
          }
          .tk-report .closing h2 { margin: 0 0 1rem; font-size: clamp(1.4rem,4vw,3rem); color: #fff; }
          .tk-report .closing p  { max-width: 100%; color: rgba(255,255,255,.84); font-size: clamp(.85rem,2vw,1.2rem); text-align: justify; }
          .tk-report .contact-grid {
            display: grid; grid-template-columns: repeat(auto-fit, minmax(min(140px,100%), 1fr));
            gap: clamp(.4rem,1.2vw,1rem); margin-top: var(--tk-space-8);
          }
          .tk-report .contact {
            background: rgba(168,16,38,.1); border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(10px,2vw,16px); padding: clamp(.6rem,1.5vw,.9rem); min-width: 0;
          }
          .tk-report .contact span   { display: block; color: rgba(255,255,255,.64); font-size: var(--tk-text-xs); margin-bottom: .3rem; }
          .tk-report .contact strong { font-size: clamp(.78rem,1.6vw,.92rem); word-break: break-word; }
          .tk-report .light { color: rgba(255,255,255,.68); }

          /* ── Download button ── */
          #tk-pdf-btn {
            position: fixed; bottom: 2rem; right: 2rem; z-index: 9999;
            display: inline-flex; align-items: center; gap: .6rem;
            padding: .75rem 1.4rem;
            background: linear-gradient(135deg, #F3AC26, #D79A1E);
            color: white; border: 1px solid #F3AC26; border-radius: 999px;
            box-shadow: 0 8px 24px rgba(198,40,40,.35), 0 0 14px rgba(243,173,38,.45);
            cursor: pointer; font-family: var(--tk-font); font-weight: 700; font-size: 1rem;
            transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
          }
          #tk-pdf-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(198,40,40,.45); }
          #tk-pdf-btn:active:not(:disabled) { transform: translateY(0); }
          #tk-pdf-btn:disabled { opacity: .72; cursor: not-allowed; }
          #tk-pdf-btn .btn-spinner {
            width: 16px; height: 16px;
            border: 2px solid rgba(255,255,255,.35); border-top-color: white;
            border-radius: 50%; animation: tk-spin .7s linear infinite; flex-shrink: 0;
          }

          /* ── Animations ── */
          @keyframes tk-shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
          @keyframes tk-fadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
          @keyframes tk-spin    { to{transform:rotate(360deg)} }
          .tk-fade-up { animation: tk-fadeUp .55s ease both; }

          /* ── Print ── */
          @media print {
            .tk-report { background: white; }
            .tk-report .report { max-width: none; padding: 0; }
            .tk-report .page { box-shadow: none; border-radius: 0; margin: 0 0 12px; border: 1px solid #ddd; }
            #tk-pdf-btn { display: none !important; }
          }

          /* ── Tablet (≤ 768px) ── */
          @media (max-width: 768px) {
            .tk-report .report { padding: 1.25rem .75rem 3rem; }
            .tk-report .page { border-radius: 20px; margin-bottom: 1.25rem; }
            


            .tk-report .cover { min-height: 88vh; }
            .tk-report .cover-inner { padding: 2.25rem; min-height: 88vh; }
            .tk-report .cover-title { margin-top: 2.5rem; }
            .tk-report .cover-title h2 { font-size: clamp(1.8rem, 5vw, 2.8rem); }
            .tk-report .cover-grid { gap: .75rem; margin-top: 1.5rem; }
            .tk-report .page-inner { padding: 1.75rem; }
            .tk-report .topbar { flex-wrap: wrap; gap: .5rem; }
            .tk-report .pill { font-size: 10px; padding: .35rem .6rem; }
            .tk-report .two-col { grid-template-columns: 1fr; }
            .tk-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .tk-report .bar-track { height: 18px; }
             .tk-report .bar-label {
    font-weight: 600;
  }

  .tk-report .bar-val {
    text-align: right;
  }
            .tk-report .contact-grid { grid-template-columns: repeat(auto-fit, minmax(min(140px,100%),1fr)); }
            .tk-warn-grid { grid-template-columns: 1fr; }
            .tk-band-pill { display: none !important; }
          }

          /* ── Mobile (≤ 480px) ── */
          @media (max-width: 480px) {
            .tk-report .report { padding: .75rem .5rem 2.5rem; }
            .tk-report .page { border-radius: 16px; margin-bottom: .9rem; }
            .tk-report .cover { min-height: 100svh; }
            .tk-report .cover-inner { padding: 1.25rem; min-height: 100svh; }
            


            .tk-report .cover-title { margin-top: 1.5rem; }
            .tk-report .cover-title h2 { font-size: clamp(1.5rem, 7vw, 2rem); line-height: 1.08; }
            .tk-report .cover-title p { font-size: .875rem; }
            .tk-report .cover-grid { grid-template-columns: repeat(auto-fit, minmax(min(130px,100%),1fr)); gap: .5rem; margin-top: 1.1rem; }
            .tk-report .meta { padding: .6rem .75rem; }
            .tk-report .cover-badges { margin-top: .9rem; gap: .4rem; }
            .tk-report .cover-badge { font-size: .7rem; padding: .3rem .55rem; }
            .tk-report .footer-note { flex-direction: column; gap: .2rem; font-size: .68rem; margin-top: 1rem; }
            .tk-report .page-inner { padding: 1rem; }
            .tk-report .topbar { flex-wrap: wrap; gap: .4rem; margin-bottom: 1rem; }
            .tk-report .section-title { font-size: 1.25rem; }
            .tk-report .pill { font-size: 9px; padding: .25rem .5rem; }
            .tk-report .summary-grid { gap: .75rem; }
            .tk-report .card { padding: .875rem; border-radius: 14px; }
            .tk-report .card h3 { font-size: .95rem; margin-bottom: .45rem; }
            .tk-report .lead { font-size: .84rem; }
            .tk-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .tk-report .bar-label { font-size: .77rem; font-weight: 600; }
            .tk-report .bar-track { height: 14px; }
            .tk-report .bar-track { height: 10px; }
            .tk-report .bar-val { font-size: .77rem; text-align:right;}
            .tk-report .score-tile-grid { grid-template-columns: repeat(2, 1fr); }
            .tk-report .growth-grid { grid-template-columns: 1fr; }
            .tk-warn-grid { grid-template-columns: 1fr; }
            .tk-report .contact-grid { grid-template-columns: 1fr; gap: .5rem; margin-top: 1rem; }
            .tk-report .closing h2 { font-size: clamp(1.45rem,5.5vw,2rem); }
            .tk-report .closing p { font-size: .88rem; }
          }

          /* ── Tiny (≤ 390px) ── */
          @media (max-width: 390px) {
            .tk-report .report { padding: .5rem .35rem 2rem; }
            .tk-report .page { border-radius: 12px; margin-bottom: .75rem; }
            


            .tk-report .cover { min-height: 100svh; }
            .tk-report .cover-inner { padding: 1rem; min-height: 100svh; }
            .tk-report .brand { padding-right: clamp(0px,44vw,160px); }
            .tk-report .badge { font-size: .55rem; padding: .25rem .5rem; }
            .tk-report .cover-title { margin-top: 1rem; }
            .tk-report .cover-title h2 { font-size: clamp(1.35rem,6.5vw,1.7rem); line-height: 1.1; max-width: 100%; }
            .tk-report .cover-title p { font-size: .78rem; }
            .tk-report .cover-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .9rem; }
            .tk-report .meta { padding: .5rem .65rem; border-radius: 12px; }
            .tk-report .meta strong { font-size: .82rem; }
            .tk-report .cover-badges { margin-top: .75rem; gap: .3rem; }
            .tk-report .cover-badge { font-size: .65rem; padding: .25rem .45rem; }
            .tk-report .footer-note { flex-direction: column; gap: .15rem; font-size: .62rem; margin-top: .75rem; }
            .tk-report .page-inner { padding: .85rem; }
            .tk-report .bar-row { grid-template-columns: 1fr; gap: .35rem; }
            .tk-report .contact-grid { grid-template-columns: 1fr; gap: .4rem; margin-top: .75rem; }
             .tk-report .bar-label {
    font-weight: 600;
  }

  .tk-report .bar-val {
    text-align: right;
  }
          }
        `}</style>

        {/* ── Floating PDF Button ── */}
        <button id="tk-pdf-btn" onClick={handleDownloadPDF} disabled={isGenerating || loading || !scores} aria-label="Download PDF Report">
          {isGenerating ? (
            <><span className="btn-spinner" />Generating PDF…</>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download PDF
            </>
          )}
        </button>

        <div className="report">

          {/* ══════════════════════════════════════════════════════
              PAGE 1 — COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page cover">
            <div className="cover-inner">

              {/* Logo panel */}
              <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

              <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", flex:1, position:"relative", zIndex:2 }}>
                <div className="cover-title">
                  <h2>Thomas-Kilmann <br></br>Conflict Mode Assessment</h2>
                  <p>
                    A comprehensive individual profile report covering five Thomas-Kilmann conflict-handling modes and their situational application, mapping your behavioural tendencies across assertiveness and cooperativeness dimensions.
                  </p>
                </div>

                <div className="cover-grid">
                  <div className="meta"><span>Participant</span><strong>{loading ? "—" : (reportMeta?.orgName || "—")}</strong></div>
                  <div className="meta"><span>Assessment Date</span><strong>{loading ? "—" : (reportMeta?.period || "—")}</strong></div>
                  <div className="meta"><span>Dominant Mode</span><strong>{loading ? "—" : (dominantLabels || "—")}</strong></div>
                </div>

                {/* <div className="cover-grid" style={{ marginTop:"1rem" }}>
                  <div className="meta"><span>Instrument</span><strong>Thomas-Kilmann (TKI)</strong></div>
                  <div className="meta"><span>Report Type</span><strong>Individual Conflict Mode Profile</strong></div>
                  <div className="meta"><span>Score Range</span><strong>0 – 12 per mode</strong></div>
                </div> */}

                <div className="cover-badges">
                  {["Executive Dashboard","Five Conflict Modes","Normative Comparison","Growth Strategy"].map(b => (
                    <span key={b} className="cover-badge">{b}</span>
                  ))}
                </div>
              </div>

              <div className="footer-note" style={{ position:"relative", zIndex:2 }}>
                <div>Based on the Thomas-Kilmann Conflict Mode Instrument (TKI) framework.</div>
                <div>Each mode scored out of 12.</div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 2 — EXECUTIVE DASHBOARD
          ══════════════════════════════════════════════════════ */}
          <section className="page tk-fade-up">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Executive Dashboard</h2>
                </div>
                <div className="pill pill-red">Dominant: {loading ? "—" : (dominantLabels || "—")}</div>
              </div>

              <p className="lead" style={{ marginBottom:"1.5rem" }}>
                This section presents your scores across all five conflict-handling modes. Scores reflect how frequently you tend to use each mode when facing conflict situations — higher scores indicate greater reliance on that approach.
              </p>

              <div className="two-col" style={{ marginBottom:"1.5rem" }}>
                {/* Left — Pentagon radar */}
                <div className="sub-panel">
                  <h4>Your Conflict Mode Profile</h4>
                  <TKRadarChart scores={scores} loading={loading} />
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".4rem", marginTop:".65rem" }}>
                    {MODE_DEFINITIONS.map(def => (
                      <div key={def.key} style={{ display:"flex", alignItems:"center", gap:".4rem" }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:def.color, flexShrink:0 }} />
                        <span style={{ fontSize:"var(--tk-text-xs)", color:T.slate }}>{def.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right — Score tiles + dominant mode + callout */}
                <div className="sub-panel">
                  <h4>Mode Scores &amp; Evaluation Insight</h4>
                  <p style={{ color:T.slate, fontSize:"var(--tk-text-xs)", margin:"0 0 .75rem" }}>
                    Scores reflect self-reported frequency of use across 30 paired-choice items (0–12 per mode).
                  </p>
                  <div className="score-tile-grid" style={{ marginBottom:".85rem" }}>
                    {MODE_DEFINITIONS.map(def => (
                      <div key={def.key} className="score-tile">
                        <div className="score-tile-label">{def.label}</div>
                        {loading
                          ? <Skeleton h={34} r={6} />
                          : <>
                              <div className="score-tile-value" style={{ color:def.color }}>
                                {scores?.[def.key] ?? "—"}<span style={{ fontSize:".72rem", color:T.slate, fontWeight:400 }}>/12</span>
                              </div>
                              <div className="score-tile-band" style={{ color:def.color }}>
                                {scores ? scoreBandLabel(def.key, scores[def.key]) : ""}
                              </div>
                            </>
                        }
                      </div>
                    ))}
                  </div>

                  {!loading && dominantModes.length > 0 && (
                    <div style={{
                      background:`linear-gradient(135deg,${T.navy}10,${T.blue}08)`,
                      border:`1px solid ${T.navy}30`, borderRadius:12, padding:"10px 14px", marginBottom:".75rem",
                    }}>
                      <div style={{ fontSize:"var(--tk-text-xs)", fontWeight:700, color:T.slate, textTransform:"uppercase", letterSpacing:".07em" }}>
                        Dominant Mode{dominantModes.length > 1 ? "s" : ""}
                      </div>
                      <div style={{ fontSize:"1.1rem", fontWeight:700, color:T.navy, marginTop:2 }}>
                        {dominantModes.map(k => MODE_DEFINITIONS.find(d => d.key===k)?.label).join(" · ")}
                      </div>
                    </div>
                  )}
                  {loading && <Skeleton h={60} r={12} />}

                  <Callout>
                    <strong>Evaluation insight: </strong>
                    {loading ? <Skeleton h={16} w="90%" r={4} /> : insightText}
                  </Callout>
                </div>
              </div>

              {/* Mode scores bar chart + Two dimensions */}
              <div className="two-col">
                <div className="card">
                  <h3>Mode Scores at a Glance</h3>
                  <div style={{ marginTop:".5rem" }}>
                    {MODE_DEFINITIONS.map(def => {
                      const val = scores?.[def.key] ?? 0;
                      const pct = Math.min(100, (val / 12) * 100);
                      return (
                        <div key={def.key} className="bar-row">
                          <span className="bar-label">{def.label}</span>
                          <div className="bar-track">
                            <div className="bar-fill" style={{ width: loading ? "0%" : `${pct}%`, background: def.color }} />
                          </div>
                          <span className="bar-val" style={{ color:def.color }}>{loading ? "—" : `${val}/12`}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="card">
                  <h3>The Two Dimensions of Conflict</h3>
                  <p className="lead" style={{ marginBottom:".75rem" }}>All five conflict modes are defined by two underlying dimensions:</p>
                  <div style={{ display:"grid", gap:".6rem" }}>
                    {[
                      { label:"Assertiveness →", color:M.competing, text:"The extent to which you attempt to satisfy your own concerns in a conflict. High assertiveness modes: Competing, Collaborating. Low assertiveness: Avoiding, Accommodating." },
                      { label:"Cooperativeness ↑", color:M.collaborating, text:"The extent to which you attempt to satisfy the other person's concerns. High cooperativeness modes: Collaborating, Accommodating. Low cooperativeness: Competing, Avoiding." },
                      { label:"The Middle Ground", color:M.compromising, text:"Compromising falls in the middle of both axes — intermediate in assertiveness and cooperativeness. It is the default mode when a balance between both parties is needed quickly." },
                    ].map(({ label, color, text }) => (
                      <div key={label} style={{ background:"#fbfdff", border:`1px solid ${T.line}`, borderRadius:12, padding:"10px 14px" }}>
                        <div style={{ fontWeight:700, color, marginBottom:4, fontSize:"var(--tk-text-sm)" }}>{label}</div>
                        <p style={{ margin:0, fontSize:"var(--tk-text-xs)", color:T.ink, lineHeight:1.6, textAlign:"justify" }}>{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 3 — GRAPHING YOUR TKI SCORES + GROWTH PLAN
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Graphing Your TKI Scores</h2>
                </div>
                <div className="pill pill-slate">Normative Comparison</div>
              </div>

              <div className="card" style={{ marginBottom:"1.5rem" }}>
                <p className="lead">
                  The chart below plots your score in each mode against the original TKI norm group — managers at middle and upper levels of business and government. The two <strong>bold dotted lines</strong> in each column mark the boundaries between the <strong>High 25%</strong>, <strong>Middle 50%</strong>, and <strong>Low 25%</strong> bands. Your score appears as a filled circle.
                </p>
              </div>
              <TKPercentileChart scores={scores} />

              <div style={{ marginTop:"2rem" }}>
                <div className="topbar" style={{ marginBottom:"1rem" }}>
                  <div>
                    <div className="section-kicker">Growth</div>
                    <h2 className="section-title" style={{ fontSize:"var(--tk-text-xl)" }}>Developing Your Conflict Repertoire</h2>
                  </div>
                </div>

                <p className="lead" style={{ marginBottom:"1.25rem" }}>
                  The TKI is not a fixed snapshot — it is a dynamic model that should evolve as your career progresses, your team dynamics change, and the conflict situations you face shift. The following principles guide effective use of the five modes:
                </p>

                <div className="growth-grid">
                  {[
                    { label:"Expand your repertoire",  color:M.collaborating, body:"The goal is not to maximize any single mode, but to have the flexibility to deploy all five effectively. Situations differ — your approach should adapt." },
                    { label:"Diagnose before reacting", color:M.compromising,  body:"Before engaging, assess what the situation requires: How important is the issue? How important is the relationship? What power dynamics are at play?" },
                    { label:"Watch for over-reliance",  color:M.competing,     body:"High scores on any mode can signal over-reliance. Review the diagnostic questions for each mode to identify whether your use is appropriate for the situations you face." },
                    { label:"Develop underused modes",  color:M.accommodating, body:"Low scores may indicate underused social skills. Practice the modes you rely on least — they are often the most valuable when the right situation arises." },
                  ].map(({ label, color, body }) => (
                    <div key={label} className="growth-card" style={{ borderLeft:`3px solid ${color}` }}>
                      <div className="growth-card-title" style={{ color }}>{label}</div>
                      <p>{body}</p>
                    </div>
                  ))}
                </div>

                <Callout>
                  <strong>Growth disclaimer: </strong>
                  This report reflects your self-reported tendencies within your current professional context. Conflict-handling behaviours are influenced by both personal predispositions and situational requirements. View these results not as a final assessment, but as a starting point for developing a more flexible and effective conflict management repertoire.
                </Callout>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 4 — CONFLICT MODE DESCRIPTIONS (Modes 1–3)
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Conflict Mode Descriptions</h2>
                </div>
                <div className="pill pill-red">Five Modes</div>
              </div>

              <div className="card" style={{ marginBottom:"1.5rem" }}>
                <h3>Understanding the Thomas-Kilmann Instrument</h3>
                <p className="lead">
                  The Thomas-Kilmann Conflict Mode Instrument (TKI) is designed to assess an individual's behaviour in conflict situations — situations in which the concerns of two people appear to be incompatible. Each of us is capable of using all five conflict-handling modes; none of us can be characterised as having a single, rigid style.
                </p>
                <p className="lead" style={{ marginTop:"1rem" }}>
                  Your score profile indicates the <strong>repertoire of conflict-handling skills</strong> you use in the kinds of conflict situations you face. The higher the score for each mode, the more frequently you tend to use it. Extreme scores — high or low — are not necessarily problematic, since your situation may require high or low use of a given mode.
                </p>
              </div>

              <p style={{ color:T.slate, fontSize:"var(--tk-text-sm)", margin:"0 0 1rem" }}>
                Expand each mode below to explore its description, situational uses, and diagnostic notes for high and low scores. Your dominant mode{dominantModes.length > 1 ? "s are" : " is"} highlighted.
              </p>

              <div style={{ display:"grid", gap:".75rem" }}>
                {MODE_DEFINITIONS.slice(0, 6)
                  .slice()
                  .sort((a, b) => (scores?.[b.key] ?? -1) - (scores?.[a.key] ?? -1))
                  .map(def => (
                    <ModeCard
                      key={def.key}
                      def={def}
                      score={scores?.[def.key] ?? null}
                      band={scores ? scoreBandLabel(def.key, scores[def.key]) : null}
                      isDominant={dominantModes.includes(def.key)}
                    />
                  ))}
              </div>

              <div style={{ marginTop:"1.5rem" }}>
                <Callout>
                  <strong>There are no universal right answers.</strong> All five modes are useful — each represents a set of valuable social skills. The effectiveness of a given conflict-handling mode depends upon the requirements of the specific conflict situation and the skill with which the mode is used. Our conventional wisdom recognises: "Two heads are better than one" (Collaborating), "Kill your enemies with kindness" (Accommodating), "Split the difference" (Compromising), "Leave well enough alone" (Avoiding), and "Might makes right" (Competing).
                </Callout>
              </div>

              <div style={{ marginTop:"1rem", background:"#f8f4eb", border:`1px solid ${T.gold}55`, borderRadius:"14px", padding:"1rem" }}>
                <div style={{ fontSize:".72rem", color:T.gold, fontWeight:700, textTransform:"uppercase", letterSpacing:".1em", marginBottom:".5rem" }}>
                  Context &amp; Meaning
                </div>
                <p style={{ margin:0, fontSize:"var(--tk-text-sm)", color:T.ink, lineHeight:1.6 }}>
                  There are no universal right answers. All five modes are useful — each represents a set of valuable social skills. The effectiveness of any mode depends on the specific conflict situation and the skill with which it is deployed.
                </p>
              </div>
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              PAGE 5 — CONFLICT MODE DESCRIPTIONS (Modes 4–5)
          ══════════════════════════════════════════════════════ */}
          {/* <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">Conflict Mode Descriptions</h2>
                </div>
                <div className="pill pill-red">Five Modes</div>
              </div>

              <p style={{ color:T.slate, fontSize:"var(--tk-text-sm)", margin:"0 0 1rem" }}>
                Continued from page 4 — the remaining two conflict-handling modes, with situational uses and high/low diagnostic notes.
              </p>

              <div style={{ display:"grid", gap:".75rem" }}>
                {MODE_DEFINITIONS.slice(3).map(def => (
                  <ModeCard
                    key={def.key}
                    def={def}
                    score={scores?.[def.key] ?? null}
                    band={scores ? scoreBandLabel(def.key, scores[def.key]) : null}
                  />
                ))}
              </div>

              
            </div>
          </section> */}

          {/* ══════════════════════════════════════════════════════
              PAGE 6 — ASSERTIVENESS & COOPERATIVENESS MODEL
          ══════════════════════════════════════════════════════ */}
          <section className="page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">Assertiveness &amp; Cooperativeness Model</h2>
                </div>
                <div className="pill pill-slate">Mode Positioning</div>
              </div>

              <div className="card" style={{ marginBottom:"1.5rem" }}>
                <h3>Your Scores on the Assertiveness–Cooperativeness Model</h3>
                <p className="lead">
                  The Thomas-Kilmann model maps all five conflict-handling modes onto two underlying dimensions: <strong>assertiveness</strong> (pursuing your own concerns) and <strong>cooperativeness</strong> (satisfying the other person's concerns). Your combined scores are plotted below — the filled circle marks your position, and the highlighted mode box shows your active region. 
                </p>
              </div>

              <TKPositionGrid scores={scores} />

              <div className="two-col" style={{ marginTop:"1.5rem" }}>
                <div className="card">
                  <h3>Understanding Your Scores</h3>
                  <p className="lead">
                    The Thomas-Kilmann model shows that each of the five modes reflects a learned pattern of behaviour shaped by predisposition and past success. Rather than being fixed traits, these modes are most effective when used situationally — allowing you to respond flexibly to changing contexts, team dynamics, and the importance of the issue at stake.
                  </p>
                </div>
                <div className="card">
                  <h3>Coaching Note</h3>
                  <p className="lead">
                    This report is most powerful when reviewed in a guided debrief with a manager, coach, or facilitator. The goal is not to determine where you stand today, but to identify which behaviours to strengthen and which combinations to deploy more deliberately across the situations you face.
                  </p>
                </div>
              </div>

              {/* Error state */}
              {error && (
                <div style={{ background:"#fff0f0", border:"1px solid #f5c6c6", borderRadius:12, padding:"14px 18px", color:"#9b1c1c", fontSize:"var(--tk-text-sm)", marginTop:"1rem" }}>
                  ⚠️ {error}
                </div>
              )}
            </div>
          </section>

          {/* ══════════════════════════════════════════════════════
              BACK COVER
          ══════════════════════════════════════════════════════ */}
          <section className="page back-cover">
            <div style={{
              background:`radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B)`,
              minHeight:"56vh",
            }}>
              <div className="back-cover-header">
                <div style={{ fontSize:"0.72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:"rgba(255,255,255,0.66)" }}>
                  {/* Back Cover */}
                </div>
                <div style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.5)", letterSpacing:".05em" }}>Acumen Business Catalyst</div>
              </div>

              <div className="page-inner">
                <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", flex:1 }}>
                  <div className="closing" style={{ padding:"0.1rem" }}>
                    <h2>Building Flexible, Adaptive Conflict Management</h2>
                    <p>
                      Conflict-handling behaviour is not fixed. It is a repertoire that expands through self-awareness, deliberate practice, and consistent reflection. Acumen Business Catalyst uses assessments like this to turn behavioural data into focused development conversations that drive lasting change.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop:"1.5rem", background:"rgba(255,255,255,.06)", borderColor:"rgba(255,255,255,.14)", color:"white" }}>
                    <h3 style={{ color:"white" }}>Practical Conflict Design</h3>
                    <p className="lead" style={{ color:"rgba(255,255,255,.84)", textAlign:"justify" }}>
                      For this participant, the most effective conflict management approach should draw on their dominant mode as a natural starting point, while deliberately practising the modes that are under-utilised. Each of the five modes serves a purpose — the goal is not to change who you are, but to expand your repertoire so you can respond flexibly across different people, pressures, and situations.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership · Culture · Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>TK Conflict Mode Profile</strong></div>
                  </div>

                  <p className="light" style={{ marginTop:"1.5rem", fontSize:"0.75rem" }}>
                    This report is generated from the Thomas-Kilmann Conflict Mode Instrument (TKI) assessment framework. © 2026 Acumen Business Catalyst.
                  </p>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>

      {/* ── Hidden PDF generator ── */}
      {pdfData && (
        <HiddenTKReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
}